import React, { useCallback, useMemo } from 'react';
import { useSyncSingleMatterModal } from './useSyncSingleMatterModal';
import { useIsClioAuthenticated } from '~/src/hooks/useClio';
import { useAIFieldMapperEnabled } from '~/src/entities/user';
import useMst from '~/src/hooks/useMst';
import analyticsService from '~/src/services/analytics';
import { safeSetItem } from '~/src/hooks/useLocalStorage';

export type SyncSource = 'footer' | 'coreField';

type SingleMatterSyncContextType = {
  sync: (source?: SyncSource) => void;
  canSync: boolean;
};

const SingleMatterSyncContext = React.createContext<
  SingleMatterSyncContextType | undefined
>(undefined);

type SingleMatterSyncProviderProps = React.PropsWithChildren<{
  clioMatterId?: string;
  onSynced?: () => void;
}>;

const SYNC_CONFIG = {
  footer: {
    analyticsEventName: 'Populate Page',
    variant: 'populate' as const,
  },
  coreField: {
    analyticsEventName: 'Core Field Badge',
    variant: 'coreField' as const,
  },
};

export const SingleMatterSyncProvider = ({
  clioMatterId,
  onSynced,
  children,
}: SingleMatterSyncProviderProps) => {
  const handleSynced = useCallback(() => {
    safeSetItem('populate_sync_reload', 'true');
    window.location.reload();
    onSynced?.();
  }, [onSynced]);

  // Footer sync modal
  const [{ sync: syncFooter }, footerModal] = useSyncSingleMatterModal(
    SYNC_CONFIG.footer.analyticsEventName,
    handleSynced,
    {
      onMutate: ({ clio_matter_id }) => {
        analyticsService.track(
          `Single Matter Sync from ${SYNC_CONFIG.footer.analyticsEventName} - Initiated`,
          {
            clioMatterId: clio_matter_id,
          },
        );
      },
    },
    {
      variant: SYNC_CONFIG.footer.variant,
      alwaysRequireConfirm: true,
      hideCheckbox: true,
    },
  );

  // Core field badge sync modal
  const [{ sync: syncCoreField }, coreFieldModal] = useSyncSingleMatterModal(
    SYNC_CONFIG.coreField.analyticsEventName,
    handleSynced,
    {},
    {
      variant: SYNC_CONFIG.coreField.variant,
      alwaysRequireConfirm: true,
      hideCheckbox: true,
    },
  );

  const triggerSync = useCallback(
    (source: SyncSource = 'footer') => {
      if (!clioMatterId) return;

      if (source === 'coreField') {
        syncCoreField(clioMatterId);
      } else {
        syncFooter(clioMatterId);
      }
    },
    [clioMatterId, syncFooter, syncCoreField],
  );

  // Determine canSync based on auth and feature flags
  const isAuthenticatedWithClio = useIsClioAuthenticated();
  const isAIFieldMapperEnabled = useAIFieldMapperEnabled();
  const { clioSyncStatus, singleMatterSyncFailed } = useMst((store) => ({
    clioSyncStatus: store.clio.clioSyncStatus,
    singleMatterSyncFailed: store.clio.singleMatterSyncFailed,
  }));

  const hasClioSyncError = clioSyncStatus === 'error' || singleMatterSyncFailed;

  const canSync = useMemo(
    () =>
      isAIFieldMapperEnabled &&
      !!clioMatterId &&
      !!isAuthenticatedWithClio &&
      !hasClioSyncError,
    [
      isAIFieldMapperEnabled,
      clioMatterId,
      isAuthenticatedWithClio,
      hasClioSyncError,
    ],
  );

  const value = useMemo(
    () => ({
      sync: triggerSync,
      canSync,
    }),
    [triggerSync, canSync],
  );

  return (
    <SingleMatterSyncContext.Provider value={value}>
      {children}
      {footerModal}
      {coreFieldModal}
    </SingleMatterSyncContext.Provider>
  );
};

export const useSingleMatterSync = () => {
  const context = React.useContext(SingleMatterSyncContext);
  if (!context) {
    throw new Error(
      'useSingleMatterSync must be used within a SingleMatterSyncProvider',
    );
  }
  return context;
};
